import React, { useState } from 'react';
import { FaUser, FaExclamationTriangle, FaTrash } from 'react-icons/fa';
import UserRoleBadge from './UserRoleBadge.jsx';
import LoadingSpinner from './LoadingSpinner.jsx';
import { useLocale } from '../../contexts/LocaleContext.jsx';
import '../../styles/partial/ModeratorList.css';

const ModeratorList = ({ 
  moderators, 
  onRemoveModerator, 
  isLoading, 
  currentUserId 
}) => {
  const [removingId, setRemovingId] = useState(null);
  const { t, format } = useLocale();

  const handleRemoveModerator = async (moderator) => {
    if (removingId) return;

    const username = moderator.User.Username;
    const confirmMessage = t('moderators.list.confirmRemove', { username });
    if (!window.confirm(confirmMessage)) return;

    setRemovingId(moderator.UserID);
    
    try {
      // Use moderator.UserID (the user's ID) for the API call
      await onRemoveModerator(moderator.UserID);
    } catch (error) {
      console.error('Error removing moderator:', error);
    } finally {
      setRemovingId(null);
    }
  };

  /** "Sep 15, 2026" in the active locale; Unknown when the value is missing or unparseable. */
  const formatDate = (dateString) => format.date(dateString, 'short') || t('common.status.unknown');

  if (isLoading) {
    return (
      <div className="moderator-list-loading">
        <LoadingSpinner />
        <p>{t('moderators.list.loading')}</p>
      </div>
    );
  }

  if (!moderators || moderators.length === 0) {
    return (
      <div className="moderator-list-empty">
        <FaUser className="moderator-list-empty-icon" />
        <p>{t('moderators.list.emptyTitle')}</p>
        <span className="moderator-list-empty-hint">
          {t('moderators.list.emptyHint')}
        </span>
      </div>
    );
  }

  return (
    <div className="moderator-list">
      <div className="moderator-list-header">
        <h3>{t('moderators.list.heading', { count: moderators.length, max: 10 })}</h3>
        {moderators.length >= 10 && (
          <div className="moderator-list-warning">
            <FaExclamationTriangle />
            <span>{t('moderators.list.limitReached')}</span>
          </div>
        )}
      </div>

      <div className="moderator-list-items">
        {moderators.map((moderator) => {
          const userId = moderator.UserID;
          const username = moderator.User.Username;
          const dateAdded = moderator.DateAdded;
          const addedBy = moderator.AddedByUser.Username;

          return (
            <div key={userId} className="moderator-item">
              <div className="moderator-info">
                <div className="moderator-primary">
                  <div className="moderator-username">
                    <FaUser className="moderator-avatar" />
                    {username}
                  </div>
                  <UserRoleBadge role="moderator" size="small" />
                </div>
                
                <div className="moderator-secondary">

                  <span className="moderator-date">
                    {t('moderators.list.added', { date: formatDate(dateAdded) })}
                  </span>
                </div>
              </div>

              <div className="moderator-actions">
                <button
                  className="moderator-remove-button"
                  onClick={() => handleRemoveModerator(moderator)}
                  disabled={removingId === userId}
                  title={t('moderators.list.removeTitle')}
                >
                  {removingId === userId ? (
                    <LoadingSpinner size="small" />
                  ) : (
                    <div style={{ 
                      color: 'white', 
                      fontSize: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <FaTrash />
                    </div>
                  )}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Fix module export issue
export default ModeratorList;